import React, {FC, useEffect} from 'react';
import NoData from '../../Common/NoData';
import WidgetStats from '../../Match/WidgetStats';
import useStatsData from '../../../hooks/useStatsData';
import {TeamInfo} from '../../../utils/types';
import Loader from '../../Common/Loader';
import usePolling from '../../../hooks/usePolling';
import getConfig from '../../../getConfig';

interface AsyncWidgetStatsProps {
  matchId: string;
  teams: {
    home: TeamInfo;
    away: TeamInfo;
  };
}

const {pollingRates} = getConfig();

/**
 * Wraps up the business logic of fetching the current match stats only when the "stats" tab is
 * displayed (mounted into the DOM), then passes the data down to the WidgetStats component for
 * visualization.
 */
const AsyncWidgetStats: FC<AsyncWidgetStatsProps> = (props: AsyncWidgetStatsProps) => {
  const {matchId, teams} = props;
  const {withPolling} = usePolling();
  const [statsDataState, {getStatsDataByEventId}] = useStatsData();
  const repeatStatsDataByEventId = withPolling(getStatsDataByEventId, pollingRates.stats, true);

  useEffect(() => {
    repeatStatsDataByEventId(matchId);
    return repeatStatsDataByEventId.cancel;
  }, [matchId, repeatStatsDataByEventId]);

  if (!statsDataState.data && statsDataState.loading) return <Loader />;
  if (!statsDataState.data || !statsDataState.data.length) return <NoData />;

  return <WidgetStats statsData={statsDataState.data} teams={teams} />;
};

export default React.memo(AsyncWidgetStats);
